import { useCallback, useEffect, useState } from 'react';
import { useSearchParams, useLocation, useParams } from 'react-router-dom';
import type { AppointmentResponse } from '@minibook/shared';
import { buildShortManageUrl, formatPhone, type PublicAppMode } from '@minibook/shared';
import { ApiError } from '../../shared/api/client.js';
import { useTranslation } from '../../i18n/useTranslation';
import { getManageAppointment, getPublicProfile, updateManageAppointmentChannels } from '../../shared/api/bookingApi';
import type { ManageAppointmentDetails, PublicOptionalClientChannelKey, PublicProfile } from '../../shared/api/bookingApi';
import { AppIcon } from '../../shared/ui/Icon';
import { formatDuration, formatPrice, getIntlLocale, getPriceLocale } from '../../shared/formatting.js';
import { DEFAULT_TIMEZONE } from '../../shared/constants/booking.js';
import { ClientNotificationChannelsCard } from '../../shared/components/ClientNotificationChannelsCard.js';
import NotFound from './NotFound';
import { LanguageSwitcher } from '../components/LanguageSwitcher.js';
import '../styles/booking.css';

function getPublicAppMode(hostname: string): PublicAppMode {
  return hostname.includes('dev-m') || hostname.includes('localhost') || hostname.includes('127.0.0.1')
    ? 'development'
    : 'production';
}

export default function SuccessPage() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { slug } = useParams<{ slug: string }>();
  const { t, language } = useTranslation();
  const appointmentId = searchParams.get('appointmentId');
  const manageToken = searchParams.get('token');
  const shortCode = searchParams.get('shortCode');
  const vkOAuthFailed = searchParams.get('vkOAuthFailed') === '1';
  const isCanceled = searchParams.get('canceled') === 'true' || searchParams.get('canceled') === '1';
  const isRescheduled = searchParams.get('rescheduled') === 'true' || searchParams.get('rescheduled') === '1';

  const [appointment] = useState<AppointmentResponse | null>(location.state?.appointment || null);
  const clientEmailFromState = (location.state as { clientEmail?: string } | null)?.clientEmail;

  const [loading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [manageUrl, setManageUrl] = useState<string>('');
  const [copied, setCopied] = useState(false);
  const [profile, setProfile] = useState<PublicProfile | null>(null);
  const [profileNotFound, setProfileNotFound] = useState(false);
  const [clientChannels, setClientChannels] = useState<ManageAppointmentDetails['clientChannels']>([]);
  const [resolvedClientEmail, setResolvedClientEmail] = useState<string | null>(clientEmailFromState || null);
  const [pendingChannel, setPendingChannel] = useState<PublicOptionalClientChannelKey | null>(null);
  const [pendingEmailRemoval, setPendingEmailRemoval] = useState(false);

  const loadManageDetails = useCallback(async () => {
    if (!appointmentId || !manageToken) return;
    try {
      const details = await getManageAppointment(appointmentId, manageToken);
      setClientChannels(details.clientChannels);
      setResolvedClientEmail(details.clientEmail ?? clientEmailFromState ?? null);
    } catch {
      // ignore client channel refresh errors on success page
    }
  }, [appointmentId, clientEmailFromState, manageToken]);

  useEffect(() => {
    if (!slug) return;
    getPublicProfile(slug)
      .then((p) => {
        setProfile(p);
        setProfileNotFound(false);
      })
      .catch((err: any) => {
        if (err?.error?.code === 'not_found') {
          setProfileNotFound(true);
          return;
        }
      });
  }, [slug]);

  useEffect(() => {
    void loadManageDetails();
  }, [loadManageDetails]);

  useEffect(() => {
    if (!appointmentId || !manageToken) return undefined;

    const handleFocus = () => {
      void loadManageDetails();
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [appointmentId, manageToken, loadManageDetails]);

  useEffect(() => {
    // Формируем manage-ссылку
    if (appointmentId && (manageToken || shortCode)) {
      const baseUrl = window.location.origin;
      if (shortCode) {
        const url = buildShortManageUrl({
          shortCode,
          mode: getPublicAppMode(window.location.hostname),
          fallbackBaseUrl: baseUrl,
        }) ?? `${baseUrl}/r/${shortCode}`;
        setManageUrl(url);
      } else if (manageToken && slug) {
        const url = `${baseUrl}/booking/${slug}/manage?appointmentId=${appointmentId}&token=${manageToken}`;
        setManageUrl(url);
      }
    }
  }, [appointmentId, manageToken, shortCode, slug]);

  const timeZone = profile?.timeZone || DEFAULT_TIMEZONE;

  // Форматирование даты (без времени)
  const formatDate = (isoString: string) => {
    const date = new Date(isoString);
    const locale = getIntlLocale(language);
    return date.toLocaleDateString(locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone,
    });
  };

  // Форматирование времени
  const formatTime = (isoString: string) => {
    const date = new Date(isoString);
    const locale = getIntlLocale(language);
    return date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', timeZone });
  };

  const priceLocale = getPriceLocale(language);

  const getStatusMessage = () => {
    if (vkOAuthFailed) {
      return {
        type: 'warning',
        title: t('booking.success.created'),
        message: t('booking.success.vkNotificationFailed'),
      };
    }

    return null;
  };

  const statusMsg = getStatusMessage();
  const titleText = isCanceled
    ? t('booking.success.canceled')
    : isRescheduled
      ? t('booking.success.rescheduled')
      : statusMsg?.title || t('booking.success.created');
  const emptyMessageText = isCanceled
    ? t('booking.success.canceledMessage')
    : isRescheduled
      ? t('booking.success.rescheduledMessage')
      : t('booking.success.createdMessage');

  const handleChannelToggle = useCallback(
    async (channel: PublicOptionalClientChannelKey, enabled: boolean) => {
      if (!appointmentId || !manageToken) return;
      setPendingChannel(channel);
      try {
        const result = await updateManageAppointmentChannels(manageToken, {
          channels: [{ channel, enabled }],
        });
        setClientChannels(result.clientChannels);
        setResolvedClientEmail(result.clientEmail);
        setError(null);
      } catch (err) {
        const apiError = err as ApiError;
        setError(apiError.error?.message || t('booking.manage.loadError'));
      } finally {
        setPendingChannel(null);
      }
    },
    [appointmentId, manageToken, t]
  );

  const handleEmailRemove = useCallback(async () => {
    if (!manageToken) return;
    setPendingEmailRemoval(true);
    try {
      const result = await updateManageAppointmentChannels(manageToken, {
        channels: [{ channel: 'email', enabled: false }],
      });
      setClientChannels(result.clientChannels);
      setResolvedClientEmail(result.clientEmail);
      setError(null);
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.error?.message || t('booking.manage.loadError'));
    } finally {
      setPendingEmailRemoval(false);
    }
  }, [manageToken, t]);

  if (profileNotFound) {
    return <NotFound />;
  }

  return (
    <div className="booking-container" style={{ textAlign: 'center' }}>
      <div className="booking-card">
        <div className="booking-card-lang-row">
          <LanguageSwitcher />
        </div>
        <h1 className="booking-title" style={{ color: 'var(--color-success)', marginBottom: 'var(--spacing-xl)', fontWeight: 'var(--font-weight-regular)' }}>
          {titleText}
        </h1>

        {statusMsg && (
          <div className="booking-alert booking-alert-warning" style={{ textAlign: 'left' }}>
            <div className="booking-font-bold" style={{ marginBottom: 'var(--spacing-xs)' }}>{statusMsg.message}</div>
          </div>
        )}

        {loading && <div className="booking-loading">{t('booking.success.loadingDetails')}</div>}

        {error && (
          <div className="booking-alert booking-alert-error">
            {error}
          </div>
        )}

        {appointment ? (
          <>
            {/* Блок 1: данные клиента */}
            <div className="booking-manage-summary" style={{ marginTop: 'var(--spacing-xl)', textAlign: 'left' }}>
              <div className="booking-manage-summary-title">{t('booking.manage.booked')} ✅</div>
              <div className="booking-manage-summary-row">
                <span className="booking-manage-summary-label">{t('booking.success.name')}:</span>
                <span>{appointment.clientName}</span>
              </div>
              <div className="booking-manage-summary-row">
                <span className="booking-manage-summary-label">{t('booking.success.phone')}:</span>
                <span>{appointment.clientPhone ? formatPhone(appointment.clientPhone) : '—'}</span>
              </div>
              <div className="booking-manage-summary-row">
                <span className="booking-manage-summary-label">{t('booking.success.date')}:</span>
                <span>{formatDate(appointment.startAt)}</span>
              </div>
              <div className="booking-manage-summary-row">
                <span className="booking-manage-summary-label">{t('booking.success.time')}:</span>
                <span>{formatTime(appointment.startAt)} - {formatTime(appointment.endAt)}</span>
              </div>
              {appointment.orderedServices && appointment.orderedServices.length > 0 && (
                <div className="booking-manage-summary-row">
                  <span className="booking-manage-summary-label">{t('booking.success.services')}:</span>
                  <div className="booking-flex-col booking-gap-sm" style={{ flex: 1 }}>
                    {appointment.orderedServices.map((service: { name: string; durationMin?: number | null; price: number }, index: number) => (
                      <div key={index} className="booking-flex-between" style={{ gap: 'var(--spacing-md)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)', flex: 1, minWidth: 0 }}>
                          <span style={{ flex: 1, minWidth: 0 }}>{service.name}</span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-xs)', flexShrink: 0 }}>
                            <AppIcon name="timeOutline" size={16} tone="muted" />
                            <span className="booking-text-sm" style={{ color: 'var(--color-text-muted)' }}>
                              {formatDuration(service.durationMin, priceLocale)}
                            </span>
                          </div>
                        </div>
                        <div className="booking-text-sm booking-font-semibold" style={{ flexShrink: 0, textAlign: 'right' }}>
                          {formatPrice(Number(service.price), priceLocale)} {t('booking.currency')}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {appointment.comment && (
                <div className="booking-manage-summary-row">
                  <span className="booking-manage-summary-label">{t('booking.success.comment')}:</span>
                  <span>{appointment.comment}</span>
                </div>
              )}
            </div>

            {/* Блок 2: мастер */}
            {profile && (
              <div className="booking-manage-master" style={{ textAlign: 'left' }}>
                <div className="booking-manage-master-title">
                  {t('booking.manage.recordTo')}: {profile.publicTitle?.trim() || profile.name || '—'}
                </div>
                <div className="booking-manage-master-contacts">
                  {profile.phone && (
                    <div className="booking-manage-master-contact">
                      <AppIcon name="phone" size={16} tone="muted" />
                      <span>{formatPhone(profile.phone)}</span>
                    </div>
                  )}
                  {profile.address && (
                    <div className="booking-manage-master-contact">
                      <AppIcon name="location" size={16} tone="muted" />
                      <span>{profile.address}</span>
                    </div>
                  )}
                  {!profile.phone && !profile.address && <div style={{ color: 'var(--color-text-muted)' }}>—</div>}
                </div>
              </div>
            )}

            {/* Блок 3: управление записью */}
            <ClientNotificationChannelsCard
              channels={clientChannels}
              clientEmail={resolvedClientEmail || undefined}
              pendingChannel={pendingChannel}
              pendingEmailRemoval={pendingEmailRemoval}
              onToggle={handleChannelToggle}
              onEmailRemove={handleEmailRemove}
            />
            <div className="booking-manage-master" style={{ textAlign: 'left' }}>
              <div className="booking-manage-master-title">{t('booking.success.manageTitle')}</div>
              {manageUrl ? (
                <>
                  <div className="booking-manage-link-input-group" style={{ marginTop: 'var(--spacing-lg)' }}>
                    <input
                      type="url"
                      readOnly
                      inputMode="none"
                      autoComplete="off"
                      value={manageUrl}
                      className="booking-manage-link-input"
                    />
                    <button
                      onClick={() => {
                        window.open(manageUrl, '_blank');
                      }}
                      className="booking-icon-button"
                      aria-label={t('booking.success.goToLink')}
                      title={t('booking.success.go')}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                        <polyline points="15 3 21 3 21 9"></polyline>
                        <line x1="10" y1="14" x2="21" y2="3"></line>
                      </svg>
                    </button>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(manageUrl);
                        setCopied(true);
                        setTimeout(() => setCopied(false), 2000);
                      }}
                      className={`booking-icon-button ${copied ? 'copied' : ''}`}
                      aria-label={t('booking.success.copyLink')}
                      title={copied ? t('booking.success.copied') : t('booking.success.copy')}
                    >
                      {copied ? (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"></polyline></svg>
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
                      )}
                    </button>
                  </div>
                  <div className="booking-text-sm booking-color-muted" style={{ marginTop: 'var(--spacing-sm)' }}>
                    {t('booking.success.manageHint')}
                  </div>
                </>
              ) : appointmentId && (
                <div className="booking-text-sm booking-color-muted" style={{ marginTop: 'var(--spacing-lg)' }}>
                  {t('booking.success.manageUnavailable')}
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="booking-card booking-color-muted" style={{ marginTop: 'var(--spacing-xl)' }}>
            <p>{emptyMessageText}</p>
            {appointmentId && (
              <p className="booking-text-sm booking-mt-lg">
                {t('booking.success.appointmentNumber')}: {appointmentId}
              </p>
            )}
            {!manageUrl && appointmentId && (
              <p className="booking-text-sm booking-mt-lg booking-color-muted">
                {t('booking.success.manageUnavailable')}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
